import { convertUaeToUtc, getCurrentUaeDate } from "@utils/date-time";
import { z } from "zod";

export const BAY_HIRE_REQUEST_FORM_FIELDS = {
  PORT: "port",
  BAY_PREFERENCE: "bayPreference",
  DATE_FROM: "dateFrom",
  DATE_TO: "dateTo",
  PAYMENT_TYPE: "paymentType",
  RESPONSIBLE_PERSON_NAME: "responsiblePersonName",
  RESPONSIBLE_PERSON_DESIGNATION: "responsiblePersonDesignation",
  BAY_ASSIGNMENTS: "bayAssignments",
  REMARKS: "remarks",
  ATTACHMENTS: "attachments",
} as const;

// DFT-0110: which port entity the request belongs to - required, chosen once at creation and
// immutable thereafter (see BayHireRequestForm.tsx's own disabling rule for this field).
export const PORT_OPTIONS = [
  { label: "Port of Fujairah", value: "POF" },
  { label: "Port of Dibba", value: "POD" },
] as const;

export const BAY_PREFERENCE_OPTIONS = [
  { label: "Inside Port", value: "INSIDE_PORT" },
  { label: "Outside Port", value: "OUTSIDE_PORT" },
] as const;

export const PAYMENT_TYPE_OPTIONS = [
  { label: "Monthly", value: "MONTHLY" },
  { label: "Yearly", value: "YEARLY" },
  { label: "Exempted", value: "EXEMPTED" },
] as const;

const PORT_VALUES = PORT_OPTIONS.map((option) => option.value);
const BAY_PREFERENCE_VALUES = BAY_PREFERENCE_OPTIONS.map((option) => option.value);
const PAYMENT_TYPE_VALUES = PAYMENT_TYPE_OPTIONS.map((option) => option.value);

/**
 * Start-of-day comparison only - `dateFrom`/`dateTo` are themselves normalized to Asia/Dubai
 * start-of-day before being stored (see `useBayHireRequestForm.tsx`'s `toISOInstant`), so a
 * same-day pick must not be flagged as "in the past" just because the current instant carries a
 * later time-of-day than midnight.
 *
 * **DFT-0109**: "today" is now Asia/Dubai's today, not the comparing browser's own local today -
 * the previous implementation zeroed both sides via browser-local `setHours(0,0,0,0)`, so a
 * browser outside Asia/Dubai's own timezone could disagree with it on which calendar day "today"
 * is for part of every day (matching `vessel-nomination`'s `preNominationRequestSchema.ts`, the
 * established precedent for this exact "not in the past" check). `getCurrentUaeDate()` returns a
 * `Date` whose LOCAL getters already read the Asia/Dubai wall-clock digits (its own convention,
 * shared with `convertUaeToUtc`'s input) - zeroing those local getters and feeding the result
 * through `convertUaeToUtc` gives today's real Asia/Dubai midnight as a genuine UTC instant, which
 * is then compared against `isoValue`'s own real instant with a plain epoch comparison. No local
 * `Date` getter is ever called on `isoValue` itself (a real, already-Asia/Dubai-anchored instant
 * per `toISOInstant`), so the comparison is fully independent of the comparing browser's timezone.
 */
const isBeforeToday = (isoValue: string): boolean => {
  const parsed = new Date(isoValue);
  if (Number.isNaN(parsed.getTime())) return false;
  const todayUae = getCurrentUaeDate();
  todayUae.setHours(0, 0, 0, 0);
  const todayUaeMidnightUtc = new Date(convertUaeToUtc(todayUae));
  return parsed.getTime() < todayUaeMidnightUtc.getTime();
};

/**
 * DFT-0050 (Update Request bug fix): the "not in the past" check below was written with only
 * fresh Create/Renew submissions in mind - a brand new hire period genuinely cannot start in the
 * past. Update Request and Resubmit, however, prefill `dateFrom`/`dateTo` straight from an
 * EXISTING request's own already-submitted values (`BayHireContainer.tsx`'s `initialValues`),
 * which are very often in the past by the time a vendor edits an unrelated field (e.g. replacing
 * the HOD attachment) - the backend's own `BayHireRequestValidator` has no such rule at all (only
 * "date to must be after or equal to date from"), so this was always a frontend-only UX guard, not
 * a domain one. Left unconditional, it silently failed the zod resolver's validation on submit -
 * no backend call, no visible network activity, and no error the vendor was likely to notice while
 * looking at the Attachments section rather than the Date From field above it.
 *
 * `createBayHireRequestFormSchema` now only flags a date as "in the past" when it does not match
 * the value the form was prefilled with - i.e. only when the vendor has actually changed it to a
 * NEW backdated value. Left untouched, a stale-but-unchanged existing date no longer blocks
 * submission. `bayHireRequestFormSchema` (used by the Create flow, which has no prior value to
 * compare against) keeps the original, always-on behaviour.
 */
export interface BayHireRequestFormSchemaOptions {
  originalDateFrom?: string;
  originalDateTo?: string;
}

export const createBayHireRequestFormSchema = (options: BayHireRequestFormSchemaOptions = {}) => {
  const { originalDateFrom, originalDateTo } = options;

  return (
    z
      .object({
        // DFT-0110: required - the Shipping Agent must pick a port before selecting bays, since
        // the Bays table's catalogue is scoped by it (see BaySelectionSection.tsx).
        port: z
          .string()
          .min(1, "Port is required")
          .refine((value) => (PORT_VALUES as string[]).includes(value), {
            message: "Port is required",
          }),
        bayPreference: z
          .string()
          .min(1, "Bay preference is required")
          .refine((value) => (BAY_PREFERENCE_VALUES as string[]).includes(value), {
            message: "Bay preference is required",
          }),
        dateFrom: z.string().min(1, "Date from is required"),
        dateTo: z.string().min(1, "Date to is required"),
        paymentType: z
          .string()
          .min(1, "Payment type is required")
          .refine((value) => (PAYMENT_TYPE_VALUES as string[]).includes(value), {
            message: "Payment type is required",
          }),
        responsiblePersonName: z.string().trim().min(1, "Responsible person name is required"),
        responsiblePersonDesignation: z
          .string()
          .trim()
          .min(1, "Responsible person designation is required"),
        // DFT-0044 slice C: the bays the Shipping Agent selects, replacing the free `numberOfBays` count
        // DFT-0013 added "so Operations has a reference point during pre-approval bay selection". Operations
        // no longer selects bays, so there is nothing to be a reference point FOR - and numberOfBays is now
        // DERIVED server-side from this list, so a typed count would be silently discarded. Verified against
        // the running stack: sending numberOfBays 99 with two bays stored 2.
        bayAssignments: z
          .array(
            z.object({
              referenceDataBayId: z.string().min(1),
              bayNoSnapshot: z.string().optional(),
              sizeLabel: z.string().optional(),
            }),
          )
          .min(1, "At least one bay must be selected"),
        // DFT-0078: matches the backend's own bound - `bay-hire-service/api/bay-hire-api.yaml`'s
        // `remarks` property is `maxLength: 1000` on both the create and vendor-update payloads
        // (confirmed 2026-09-08). Without this the field accepted unlimited input and only failed
        // at submission with a 400, after the user had already filled in the rest of the form.
        remarks: z.string().max(1000, "Remarks must be 1000 characters or fewer.").optional(),
        // DFT-0044 slice U (D39): staged before the request exists, since there is no request id yet to
        // attach it to (BayHireCreateAttachmentUpload). Optional at the schema level because it is only the
        // domain's own rule - not this schema's - that makes it mandatory for EXEMPTED; see the
        // superRefine below, which is the single place that rule is expressed.
        attachments: z
          .array(
            z.object({
              attachmentType: z.string(),
              documentUrl: z.string(),
              originalFileName: z.string().optional(),
              // 2026-08-25 fix: carried through so useBayHireContainer.tsx's handleUpdateFormSubmit
              // can register a freshly-staged replacement's full metadata (POST .../attachments) at
              // submit time, matching what BayHireCreateAttachmentUpload.tsx's staged upload already
              // produces - previously dropped by this schema, even though StagedAttachment has both.
              contentType: z.string().optional(),
              fileSizeBytes: z.number().optional(),
              // DFT-0050 (AC34/AC35): only present on Update Request's re-upload path (a real,
              // already-persisted attachment) - see StagedAttachment.id's own comment. Needed so the
              // edit form's submit handler can tell whether the HOD document changed, and to what.
              id: z.string().optional(),
            }),
          )
          .optional(),
      })
      // DFT-0044 D39/AC9: mirrors BayHireRequestService's own rule ("an HOD_APPROVAL document is required
      // for an EXEMPTED request") on the client, so the vendor sees this before a round trip rather than
      // after a 400. The domain enforcement is not removed or relied upon less because of this - a client
      // check is UX, not a security boundary.
      .superRefine((data, ctx) => {
        const hasHodApproval = (data.attachments ?? []).some(
          (attachment) => attachment.attachmentType === "HOD_APPROVAL",
        );
        if (data.paymentType === "EXEMPTED" && !hasHodApproval) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["attachments"],
            message: "An HOD approval document is required for an Exempted request",
          });
        }

        // Backdated hire periods make no operational sense - mirrors the picker-level
        // `dateConstraint="not-past"` guard on both fields in BayHireRequestForm.tsx, so a value
        // that somehow bypasses that (e.g. programmatic setValue, a stale initialValues seed) is
        // still caught here before submission.
        //
        // DFT-0050 (Update Request bug fix, see this function's own class doc): skipped when the
        // value is exactly the one this form was prefilled with - only a genuinely NEW backdated
        // value (one the vendor actually typed/picked) is flagged. `originalDateFrom`/
        // `originalDateTo` are undefined on the Create flow, so this is unconditionally enforced
        // there, unchanged.
        if (data.dateFrom && data.dateFrom !== originalDateFrom && isBeforeToday(data.dateFrom)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["dateFrom"],
            message: "Date from cannot be a past date",
          });
        }
        if (data.dateTo && data.dateTo !== originalDateTo && isBeforeToday(data.dateTo)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["dateTo"],
            message: "Date to cannot be a past date",
          });
        }

        // Mirrors BayHireRequestValidator's own rule server-side ("Date to must be after or equal
        // to date from") - same wording, so a vendor who somehow bypasses the picker-level
        // `minDate` guard on Date To still sees the identical message the BFF would otherwise
        // return as a 400.
        const dateToBeforeDateFrom =
          data.dateFrom &&
          data.dateTo &&
          new Date(data.dateTo).getTime() < new Date(data.dateFrom).getTime();
        if (dateToBeforeDateFrom) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["dateTo"],
            message: "Date to must be after or equal to date from",
          });
        }
      })
  );
};

// The Create flow (no prior request to compare against, so every date is "new") - also the type
// source for `BayHireRequestFormData` below, since the shape is identical regardless of options.
export const bayHireRequestFormSchema = createBayHireRequestFormSchema();

export type BayHireRequestFormData = z.infer<typeof bayHireRequestFormSchema>;

export const bayHireRequestFormDefaultValues: BayHireRequestFormData = {
  // DFT-0110 (revised): the Shipping Agent create/renew form no longer exposes a Port picker -
  // every Bay Hire request created through the SPA is for Port of Fujairah. Hardcoded here
  // (rather than left blank and set on submit) so BaySelectionSection's port-scoped catalogue
  // query and the "Add bay" enablement both see a value immediately, with no user input. Update/
  // Resubmit/Renew still override this via `initialValues.port = request.port` (BayHireRequestFormPage.tsx),
  // so an existing POD request (e.g. created some other way) keeps its own port unchanged.
  port: "POF",
  bayPreference: "",
  dateFrom: "",
  dateTo: "",
  paymentType: "",
  responsiblePersonName: "",
  responsiblePersonDesignation: "",
  bayAssignments: [],
  remarks: "",
  attachments: [],
};
